import { useQuery } from '@tanstack/react-query'
import { getCategorySpend, type AccountSummary } from '@/api/accounts'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { billCycle } from '@/lib/creditCard'
import { SectionCard, SectionHeader } from './AccountSections'

const SHORT_DATE = { day: 'numeric', month: 'short' } as const

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** What the card's current bill went on, by category. Without a bill day it falls back to this month. */
export function BillCategories({ account }: { account: AccountSummary }) {
  const { format } = useFormatCurrency()
  const today = new Date()
  const cycle = billCycle(account.billingDate, today)
  const from = cycle?.from ?? new Date(today.getFullYear(), today.getMonth(), 1)
  const to = cycle?.to ?? new Date(today.getFullYear(), today.getMonth() + 1, 0)
  const { data, isError } = useQuery({
    queryKey: ['category-spend', account.id, iso(from), iso(to)],
    queryFn: () => getCategorySpend(account.id, iso(from), iso(to)),
  })
  if (isError || !data) return null

  const total = data.reduce((sum, c) => sum + c.spent, 0)
  const largest = data[0]?.spent ?? 0
  const title = cycle
    ? `This bill · ${from.toLocaleDateString('en-IN', SHORT_DATE)} – ${to.toLocaleDateString('en-IN', SHORT_DATE)}`
    : 'This month'

  return (
    <section aria-label="Spending this bill">
      <SectionHeader
        title={title}
        right={
          <span className="text-[13px] font-medium tabular-nums text-[var(--fl-text-2)]">
            {format(total)}
          </span>
        }
      />
      <SectionCard className="py-1.5">
        {data.length === 0 ? (
          <p className="px-4 py-2 text-sm text-[var(--fl-text-3)]">
            Nothing spent on this card yet.
          </p>
        ) : (
          <ul>
            {data.map((c) => (
              <li
                key={c.categoryId ?? 'none'}
                className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-3 px-4 py-2 text-[13px]"
              >
                <span className="truncate text-[var(--fl-text-1)]">{c.name}</span>
                <span aria-hidden="true" className="h-2 rounded bg-[var(--fl-surface-subtle)]">
                  <span
                    className="block h-full rounded bg-[var(--fl-expense)] opacity-70"
                    style={{ width: `${largest > 0 ? Math.round((c.spent / largest) * 100) : 0}%` }}
                  />
                </span>
                <span className="text-right font-semibold tabular-nums text-[var(--fl-heading)]">
                  {format(c.spent)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </section>
  )
}
